import {
    useEffect,
    useRef,
    useState,
    useCallback,
    DragEvent,
    useMemo,
} from "react";
import { useForm, FormProvider } from "react-hook-form";
import {
    BULK_FIELD_FILL_LAYER,
    BULK_FIELD_SOURCE,
    FIELD_BOUNDARY_DRAW_OPTIONS,
} from "@/lib/map/layers";
import {
    addToast,
    Button,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    useDisclosure,
    NumberInput,
    Chip,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDrawPolygon,
    faFileImport,
    faFloppyDisk,
    faX,
    faMagnifyingGlassLocation,
    faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { InferType, object, string, number, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Organization } from "@/types/organization";
import { Field } from "@/types/field";
import {
    Feature,
    FeatureCollection,
    GeoJSON,
    MultiPolygon,
    Polygon,
} from "geojson";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import useAuth from "@/lib/auth/useAuth";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import { LngLatBoundsLike } from "mapbox-gl";
import { useNavigate } from "@tanstack/react-router";
import AutoCompleteInput from "../inputs/AutoCompleteInput";
import AddEditOrganizationForm from "../network/AddEditOrganizationForm";
import TextInput from "../inputs/TextInput";
import NewConnection from "./NewConnection";
import { useTranslation } from "react-i18next";
import FileInput from "../inputs/FileInput";
import shp, { FeatureCollectionWithFilename } from "shpjs";
import { filtetShpFile } from "@/lib/utils/file";
import * as turf from "@turf/turf";
import {
    addUpdateGeojsonLayers,
    addUpdateGeojsonSource,
    removeLayersIfExists,
    removeSourcesIfExists,
} from "@/lib/utils/map";
import PlssBoundarySelector from "@/components/field/PlssBoundarySelector";

const schema = object().shape({
    grower: number().required(),
    name: string().required(),
    boundary: object()
        .shape({
            id: string().optional(),
            type: string().required(),
            coordinates: array().required(),
        })
        .nullable()
        .optional(),
});
type FormType = InferType<typeof schema>;

type BoundaryFeature = Feature<Polygon | MultiPolygon>;
type AcquisitionMode = "draw" | "plss" | null;

function combineDrawnBoundary(
    collection: FeatureCollection,
): MultiPolygon | null {
    const coordinates: MultiPolygon["coordinates"] = [];
    collection.features.forEach((feature) => {
        if (feature.geometry.type === "Polygon") {
            coordinates.push(feature.geometry.coordinates);
        } else if (feature.geometry.type === "MultiPolygon") {
            coordinates.push(...feature.geometry.coordinates);
        }
    });
    return coordinates.length ? { type: "MultiPolygon", coordinates } : null;
}

const getFirstFeatureFromGeojson = (geojson: GeoJSON): Feature | null => {
    if (geojson.type === "FeatureCollection") {
        return geojson.features[0] ?? null;
    }
    if (geojson.type === "Feature") {
        return geojson;
    }
    if (geojson.type === "Polygon" || geojson.type === "MultiPolygon") {
        return {
            type: "Feature",
            properties: {},
            geometry: geojson,
        };
    }
    return null;
};

async function getBoundaryFeatureFromFile(
    file: File,
): Promise<BoundaryFeature> {
    const fileExtension = file.name
        .slice(file.name.lastIndexOf("."))
        .toLowerCase();
    let firstFeature: Feature | null | undefined;

    if (fileExtension === ".json" || fileExtension === ".geojson") {
        firstFeature = getFirstFeatureFromGeojson(
            JSON.parse(await file.text()),
        );
    } else {
        const fileData = await filtetShpFile(file);
        const fileGeoJson = (await shp(fileData)) as
            | FeatureCollectionWithFilename
            | FeatureCollectionWithFilename[];
        firstFeature = Array.isArray(fileGeoJson)
            ? fileGeoJson[0]?.features[0]
            : fileGeoJson.features[0];
    }

    if (
        firstFeature?.geometry?.type !== "Polygon" &&
        firstFeature?.geometry?.type !== "MultiPolygon"
    ) {
        throw new Error("Boundary file must contain a polygon feature.");
    }

    return firstFeature as BoundaryFeature;
}

function NewFieldForm() {
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        defaultValues: {
            grower: undefined,
            name: "",
            boundary: null,
        },
        mode: "onChange",
    });
    const { t } = useTranslation("messages");
    const boundaryFile = useRef<File | null>(null);
    const [fileName, setFileName] = useState<string | null>(null);
    const { user, isGrower, userIsSentinel, isConsultant } = useAuth();
    const canAddFarm =
        userIsSentinel ||
        (isConsultant &&
            user?.organization &&
            typeof user.organization === "object" &&
            user.organization.group_type === "service provider");
    const { map, mapLoaded } = useMap();
    const { useGetRequest, useMutateRequest } = useRequest();
    const navigate = useNavigate();
    const getFarms = useGetRequest<Organization[]>({
        url: "/organizations/farms/",
        enabled: !isGrower,
    });
    const [createdFarms, setCreatedFarms] = useState<
        Pick<Organization, "id" | "name">[]
    >([]);
    const farmOptions = useMemo(
        () =>
            [
                ...(getFarms.data ?? []),
                ...createdFarms.filter(
                    (farm) =>
                        !getFarms.data?.some(
                            (existingFarm) => existingFarm.id === farm.id,
                        ),
                ),
            ].map((farm) => ({
                id: farm.id,
                description: farm.name,
            })),
        [getFarms.data, createdFarms],
    );
    useEffect(() => {
        if (
            isGrower &&
            user?.organization &&
            typeof user.organization === "object"
        ) {
            form.setValue("grower", user.organization.id);
        }
    }, [user, isGrower, form]);
    const [grower, boundary] = form.watch(["grower", "boundary"]);
    const draw = useRef<MapboxDraw | null>(null);
    const [isDrawReady, setIsDrawReady] = useState(false);
    const [acquisitionMode, setAcquisitionMode] =
        useState<AcquisitionMode>(null);
    const [hasPlssBoundary, setHasPlssBoundary] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [isReadingBoundaryFile, setIsReadingBoundaryFile] = useState(false);

    const hasBoundaryFile = useMemo(() => fileName !== null, [fileName]);
    const hasDrawnBoundary = useMemo(
        () => boundary?.coordinates !== undefined,
        [boundary],
    );
    const isBoundaryUploadDisabled = useMemo(
        () =>
            isReadingBoundaryFile ||
            hasBoundaryFile ||
            hasDrawnBoundary ||
            acquisitionMode !== null,
        [
            isReadingBoundaryFile,
            hasBoundaryFile,
            hasDrawnBoundary,
            acquisitionMode,
        ],
    );

    const handleBoundaryFile = useCallback(
        async (file: File | null) => {
            if (!file) {
                boundaryFile.current = null;
                setFileName(null);
                if (map) {
                    removeLayersIfExists(map, [BULK_FIELD_FILL_LAYER.id]);
                    removeSourcesIfExists(map, [BULK_FIELD_SOURCE]);
                }
                return;
            }

            setIsReadingBoundaryFile(true);

            try {
                const boundaryFeature = await getBoundaryFeatureFromFile(file);
                boundaryFile.current = file;
                setFileName(file.name);

                if (map && mapLoaded && boundaryFeature) {
                    addUpdateGeojsonSource(map, BULK_FIELD_SOURCE, {
                        type: "geojson",
                        data: boundaryFeature,
                    });
                    addUpdateGeojsonLayers(map, [BULK_FIELD_FILL_LAYER]);
                    map.fitBounds(
                        turf.bbox(boundaryFeature) as LngLatBoundsLike,
                        {
                            padding: 40,
                            duration: 0,
                        },
                    );
                }
            } catch (error) {
                console.error(error);
                boundaryFile.current = null;
                setFileName(null);
                addToast({
                    title: t("addToast.invalid_shapefile"),
                    timeout: 5000,
                    color: "danger",
                });
            } finally {
                setIsReadingBoundaryFile(false);
            }
        },
        [map, mapLoaded, t],
    );

    useEffect(() => {
        return () => {
            if (map) {
                removeLayersIfExists(map, [BULK_FIELD_FILL_LAYER.id]);
                removeSourcesIfExists(map, [BULK_FIELD_SOURCE]);
            }
        };
    }, [map]);

    const handleDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);

            if (isBoundaryUploadDisabled) return;

            const file = e.dataTransfer.files?.[0];
            if (!file) return;

            void handleBoundaryFile(file);
        },
        [isBoundaryUploadDisabled, handleBoundaryFile],
    );

    const handleDragOver = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();

            if (!isBoundaryUploadDisabled) {
                setDragActive(true);
            }
        },
        [isBoundaryUploadDisabled],
    );

    const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    }, []);

    const syncDrawBoundary = useCallback(() => {
        const combined = draw.current
            ? combineDrawnBoundary(draw.current.getAll())
            : null;
        form.setValue("boundary", combined, {
            shouldDirty: true,
            shouldValidate: true,
        });
    }, [form]);

    const handleDrawCreate = useCallback(() => {
        syncDrawBoundary();
        setHasPlssBoundary(false);
        setAcquisitionMode(null);
        if (map) map.getCanvas().style.cursor = "";
    }, [map, syncDrawBoundary]);
    useEffect(() => {
        if (map && mapLoaded && !draw.current) {
            draw.current = new MapboxDraw(FIELD_BOUNDARY_DRAW_OPTIONS);
            map.addControl(draw.current, "top-left");
            map.on("draw.create", handleDrawCreate);
            map.on("draw.update", syncDrawBoundary);
            map.on("draw.delete", syncDrawBoundary);
            setIsDrawReady(true);
        }
        return () => {
            setIsDrawReady(false);
            if (map && draw.current) {
                map.off("draw.create", handleDrawCreate);
                map.off("draw.update", syncDrawBoundary);
                map.off("draw.delete", syncDrawBoundary);
                if (map.hasControl(draw.current)) {
                    map.removeControl(draw.current);
                }
                draw.current = null;
            }
        };
    }, [map, mapLoaded, handleDrawCreate, syncDrawBoundary]);

    useEffect(() => {
        if (!draw.current || !map || !mapLoaded) {
            return;
        }
        if (hasDrawnBoundary) {
            const featureId = draw.current.getAll().features[0]?.id;
            if (featureId !== undefined) {
                draw.current.changeMode("direct_select", {
                    featureId: String(featureId),
                });
            }
            map.getCanvas().style.cursor = "";
        } else if (acquisitionMode === "draw") {
            draw.current.changeMode("draw_polygon");
            map.getCanvas().style.cursor = "crosshair";
        }
    }, [acquisitionMode, hasDrawnBoundary, map, mapLoaded]);

    const selectPlssBoundary = useCallback(
        (feature: BoundaryFeature) => {
            if (!draw.current) return;
            const geometry: MultiPolygon =
                feature.geometry.type === "Polygon"
                    ? {
                          type: "MultiPolygon",
                          coordinates: [feature.geometry.coordinates],
                      }
                    : feature.geometry;
            const drawFeatures: Feature<Polygon>[] = geometry.coordinates.map(
                (coordinates) => ({
                    type: "Feature",
                    properties: {},
                    geometry: { type: "Polygon", coordinates },
                }),
            );

            draw.current.changeMode("simple_select");
            draw.current.deleteAll();
            const addedIds = drawFeatures.flatMap(
                (drawFeature) => draw.current?.add(drawFeature) ?? [],
            );
            form.setValue("boundary", geometry, {
                shouldDirty: true,
                shouldValidate: true,
            });
            if (addedIds[0] !== undefined) {
                draw.current.changeMode("direct_select", {
                    featureId: addedIds[0],
                });
            }
            if (map) {
                const [minX, minY, maxX, maxY] = turf.bbox(feature);
                map.fitBounds(
                    [
                        [minX, minY],
                        [maxX, maxY],
                    ],
                    { padding: 40 },
                );
                map.getCanvas().style.cursor = "";
            }
            setHasPlssBoundary(true);
            setAcquisitionMode(null);
        },
        [form, map],
    );
    const createField = useMutateRequest<Field>({
        method: "post",
        successMessage: t("success.created_field"),
        errorMessage: t("error.failed_to_create_field"),
        onSuccess: (data) => {
            navigate({ to: `/dashboard/fields/${data.id}/setup` });
        },
    });
    const addField = useCallback(
        (data: FormType) => {
            if (!boundaryFile.current) {
                createField.mutate({
                    url: "/fields/",
                    data: {
                        properties: {
                            name: data.name,
                            grower: data.grower,
                        },
                        geometry: {
                            type: "MultiPolygon",
                            coordinates: data.boundary?.coordinates,
                        },
                    },
                });
            } else {
                const formData = new FormData();
                formData.append("file", boundaryFile.current);
                formData.append("name", data.name);
                formData.append("grower", data.grower.toString());
                createField.mutate({
                    url: "/fields/upload/",
                    data: formData,
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
            }
        },
        [createField, boundaryFile],
    );
    const { isOpen, onOpen, onOpenChange } = useDisclosure();
    const {
        isOpen: isAddFarmOpen,
        onOpen: onAddFarmOpen,
        onClose: onAddFarmClose,
    } = useDisclosure();
    const [zoomLatitude, setZoomLatitude] = useState<number>();
    const [zoomLongitude, setZoomLongitude] = useState<number>();

    return (
        <>
            <FormProvider {...form}>
                <form
                    className="flex flex-col w-full gap-3 p-3"
                    onSubmit={form.handleSubmit(addField)}
                >
                    {!isGrower && (
                        <div className="flex items-end gap-2">
                            <div className="min-w-0 flex-1">
                                <AutoCompleteInput
                                    name="grower"
                                    label="Grower"
                                    options={farmOptions}
                                    isLoading={getFarms.isLoading}
                                    isRequired
                                    labelPlacement="outside"
                                    placeholder=" "
                                    size="sm"
                                />
                            </div>
                            {canAddFarm && (
                                <Button
                                    type="button"
                                    size="sm"
                                    color="primary"
                                    variant="flat"
                                    className="shrink-0"
                                    onPress={onAddFarmOpen}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    Add Farm
                                </Button>
                            )}
                        </div>
                    )}
                    <TextInput
                        name="name"
                        label="Field Name"
                        required
                        labelPlacement="outside"
                        placeholder=" "
                        size="sm"
                    />
                    <p className="text-sm">Boundary</p>
                    <div
                        className={`w-full flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-4 transition-colors duration-200 ${
                            dragActive
                                ? "border-primary-500 bg-primary-50"
                                : "border-gray-300 bg-gray-50"
                        }`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        style={{
                            cursor: isBoundaryUploadDisabled
                                ? "not-allowed"
                                : "pointer",
                        }}
                    >
                        <div
                            className={`flex flex-row items-center justify-center gap-2 w-full ${
                                isBoundaryUploadDisabled ? "opacity-60" : ""
                            }`}
                        >
                            <span className="text-sm text-gray-600 whitespace-nowrap">
                                Drag and drop or
                            </span>
                            <FileInput
                                accept=".geojson,.zip,.json"
                                size="sm"
                                isLoading={isReadingBoundaryFile}
                                isDisabled={isBoundaryUploadDisabled}
                                onSelect={(file) => {
                                    if (file instanceof File) {
                                        void handleBoundaryFile(file);
                                    }
                                }}
                            />
                        </div>

                        {hasBoundaryFile && (
                            <div className="flex flex-row items-center gap-2 max-w-full">
                                <Chip
                                    radius="sm"
                                    color="secondary"
                                    size="lg"
                                    classNames={{
                                        base: "max-w-full",
                                        content: "truncate",
                                    }}
                                >
                                    {fileName}
                                </Chip>
                                <Button
                                    isIconOnly
                                    size="sm"
                                    color="danger"
                                    onPress={() =>
                                        void handleBoundaryFile(null)
                                    }
                                >
                                    <FontAwesomeIcon icon={faX} />
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-row gap-2 w-full">
                        <Button
                            type="button"
                            color="primary"
                            fullWidth
                            isDisabled={isBoundaryUploadDisabled}
                            onPress={() => {
                                setHasPlssBoundary(false);
                                setAcquisitionMode("draw");
                            }}
                        >
                            <FontAwesomeIcon icon={faDrawPolygon} />
                            Draw
                        </Button>

                        <Button
                            type="button"
                            color="primary"
                            fullWidth
                            isDisabled={
                                isBoundaryUploadDisabled || !isDrawReady
                            }
                            onPress={() => {
                                draw.current?.changeMode("simple_select");
                                setAcquisitionMode("plss");
                            }}
                        >
                            Legal Description
                        </Button>

                        {(acquisitionMode !== null || hasDrawnBoundary) && (
                            <Button
                                type="button"
                                isIconOnly
                                color="danger"
                                aria-label="Clear boundary"
                                onPress={() => {
                                    draw.current?.changeMode("simple_select");
                                    if (map) map.getCanvas().style.cursor = "";
                                    draw.current?.deleteAll();
                                    form.setValue("boundary", null, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                    setHasPlssBoundary(false);
                                    setAcquisitionMode(null);
                                }}
                            >
                                <FontAwesomeIcon icon={faX} />
                            </Button>
                        )}

                        <Button
                            type="button"
                            color="primary"
                            fullWidth
                            isDisabled={isBoundaryUploadDisabled}
                            onPress={() => {
                                if (!grower) form.trigger("grower");
                                else onOpen();
                            }}
                        >
                            <FontAwesomeIcon icon={faFileImport} />
                            Import
                        </Button>
                    </div>
                    {acquisitionMode === "plss" && isDrawReady && (
                        <PlssBoundarySelector onSelect={selectPlssBoundary} />
                    )}
                    {hasPlssBoundary && hasDrawnBoundary && (
                        <p className="text-xs text-default-500">
                            Adjust the points on the map to match the field
                            before saving.
                        </p>
                    )}
                    <div className="flex flex-row gap-2 w-full items-center">
                        <NumberInput
                            label="Latitude"
                            value={zoomLatitude}
                            onValueChange={setZoomLatitude}
                            size="sm"
                            min={-90}
                            max={90}
                            hideStepper
                            formatOptions={{
                                maximumFractionDigits: 6,
                            }}
                        />
                        <NumberInput
                            label="Longitude"
                            value={zoomLongitude}
                            onValueChange={setZoomLongitude}
                            size="sm"
                            min={-180}
                            max={180}
                            hideStepper
                            formatOptions={{
                                maximumFractionDigits: 6,
                            }}
                        />
                        <Button
                            color="primary"
                            variant="flat"
                            fullWidth
                            onPress={() => {
                                if (
                                    map &&
                                    mapLoaded &&
                                    zoomLatitude &&
                                    zoomLongitude
                                ) {
                                    map.jumpTo({
                                        center: [zoomLongitude, zoomLatitude],
                                        zoom: 15,
                                    });
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faMagnifyingGlassLocation} />
                            Find Field
                        </Button>
                    </div>
                    <Button
                        type="submit"
                        color="secondary"
                        isLoading={createField.isPending}
                        isDisabled={!hasBoundaryFile && !hasDrawnBoundary}
                    >
                        <FontAwesomeIcon icon={faFloppyDisk} />
                        Add Field
                    </Button>
                </form>
            </FormProvider>
            {isAddFarmOpen && (
                <AddEditOrganizationForm
                    farmOnly
                    onClose={(shouldRefetch, createdOrganization) => {
                        if (createdOrganization) {
                            setCreatedFarms((farms) => [
                                ...farms.filter(
                                    (farm) =>
                                        farm.id !== createdOrganization.id,
                                ),
                                createdOrganization,
                            ]);
                            form.setValue("grower", createdOrganization.id, {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                        }
                        if (shouldRefetch) getFarms.refetch();
                        onAddFarmClose();
                    }}
                />
            )}
            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="lg"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <ModalHeader>Connect to a Platform</ModalHeader>
                    <ModalBody>
                        <NewConnection growerId={grower} />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

export default NewFieldForm;
